import { useState } from "react";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Box, Button, Checkbox, ListItemText, Menu, MenuItem, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { FeedCategory, FeedItem } from "../types";
import { CATEGORY, clock, FeedIcon } from "./FeedIcon";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
}));

const Title = styled(Typography)(() => ({ fontSize: "1rem", fontWeight: 700 }));

const Scroll = styled(Box, { shouldForwardProp: (p) => p !== "expanded" })<{ expanded: boolean }>(
  ({ expanded }) => ({ maxHeight: expanded ? 520 : undefined, overflowY: expanded ? "auto" : undefined }),
);

const Row = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "32px minmax(0, 1fr) auto",
  columnGap: theme.spacing(1.5),
  alignItems: "center",
  padding: theme.spacing(1, 0),
  borderTop: `1px solid ${theme.palette.divider}`,
  "&:first-of-type": { borderTop: "none" },
}));

const Kind = styled(Typography)(() => ({ fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.3 }));

const What = styled(Typography)(() => ({ fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.35 }));

const Detail = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  overflowWrap: "anywhere",
}));

const Time = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: theme.palette.text.secondary,
  fontVariantNumeric: "tabular-nums",
  alignSelf: "start",
  paddingTop: 2,
}));

const Empty = styled(Typography)(({ theme }) => ({ color: theme.palette.text.secondary, fontSize: "0.875rem" }));

const COLLAPSED = 6;
const ALL_CATEGORIES = Object.keys(CATEGORY) as FeedCategory[];

type RecentUpdatesProps = {
  items: FeedItem[];
};

/** Everything recorded for the mission, newest first: device readings (one
 *  row per packet), clinician entries, system events and new AI drafts. */
export function RecentUpdates(props: RecentUpdatesProps) {
  const [expanded, setExpanded] = useState(false);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const [shownCategories, setShownCategories] = useState<FeedCategory[]>(ALL_CATEGORIES);

  const filtered = props.items.filter((item) => shownCategories.includes(item.category));
  const rows = expanded ? filtered : filtered.slice(0, COLLAPSED);
  const filtering = shownCategories.length !== ALL_CATEGORIES.length;

  const toggle = (category: FeedCategory) =>
    setShownCategories((current) =>
      current.includes(category) ? current.filter((c) => c !== category) : [...current, category],
    );

  return (
    <Root>
      <Heading>
        <Title>Recent Updates</Title>
        <Button
          variant="outlined"
          color={filtering ? "primary" : "inherit"}
          startIcon={<FilterAltOutlinedIcon fontSize="small" />}
          onClick={(e) => setMenu(e.currentTarget)}
          sx={{ textTransform: "none", letterSpacing: 0 }}
        >
          Filter{filtering ? ` (${shownCategories.length})` : ""}
        </Button>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          {ALL_CATEGORIES.map((category) => (
            <MenuItem key={category} dense onClick={() => toggle(category)}>
              <Checkbox size="small" checked={shownCategories.includes(category)} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={CATEGORY[category].label} />
            </MenuItem>
          ))}
        </Menu>
      </Heading>

      {rows.length === 0 ? (
        <Empty>{props.items.length === 0 ? "No updates yet." : "No updates match the filter."}</Empty>
      ) : (
        <Scroll expanded={expanded}>
          {rows.map((item) => (
            <Row key={item.id}>
              <FeedIcon category={item.category} icon={item.icon} variant="soft" />
              <Box sx={{ minWidth: 0 }}>
                <Kind sx={{ color: CATEGORY[item.category].color }}>{CATEGORY[item.category].label}</Kind>
                <What>{item.title}</What>
                {item.detail ? <Detail>{item.detail}</Detail> : null}
              </Box>
              <Time>{clock(item.at)}</Time>
            </Row>
          ))}
        </Scroll>
      )}

      {filtered.length > COLLAPSED ? (
        <Button fullWidth variant="outlined" onClick={() => setExpanded(!expanded)} sx={{ mt: 1 }}>
          {expanded ? "Show recent updates" : "View all updates"}
        </Button>
      ) : null}
    </Root>
  );
}
